import React, { useState, useEffect } from 'react';
import { Mail, Clock, Globe, Power, Unplug, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import * as fbMonitorApi from '../../api/fbMonitor';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Phoenix', label: 'Arizona (No DST)' },
];

const FBMonitorSettings = ({ config, userEmail, onConfigUpdated, onDisconnect }) => {
  const [formData, setFormData] = useState({
    delivery_email: config?.delivery_email || userEmail || '',
    delivery_time: config?.delivery_time || '08:00',
    timezone: config?.timezone || 'America/New_York',
    is_active: config?.is_active ?? true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Update form when config changes
  useEffect(() => {
    if (config) {
      setFormData({
        delivery_email: config.delivery_email || userEmail || '',
        delivery_time: config.delivery_time || '08:00',
        timezone: config.timezone || 'America/New_York',
        is_active: config.is_active ?? true,
      });
    }
  }, [config, userEmail]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateTime = (time) => {
    return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
  };

  const handleSave = async () => {
    setError(null);
    setSaveSuccess(false);

    // Validation
    if (!validateEmail(formData.delivery_email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!validateTime(formData.delivery_time)) {
      setError('Please enter a valid time (HH:MM)');
      return;
    }

    setIsSaving(true);
    try {
      const updated = await fbMonitorApi.updateConfig(formData);
      onConfigUpdated(updated);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await onDisconnect();
    } catch (err) {
      setError(err.message || 'Failed to disconnect session');
    } finally {
      setIsDisconnecting(false);
      setShowDisconnectConfirm(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">
          Monitor Settings
        </h2>
        <p className="text-sm dark:text-slate-400 text-slate-500">
          Configure how and when you receive your daily lead reports.
        </p>
      </div>

      {/* Settings Form */}
      <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-6 space-y-6">
        {/* Delivery Email */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
            <Mail className="w-4 h-4 text-slate-400" />
            Delivery Email
          </label>
          <input
            type="email"
            value={formData.delivery_email}
            onChange={(e) => setFormData(prev => ({ ...prev, delivery_email: e.target.value }))}
            placeholder="your@email.com"
            className="w-full rounded-lg px-3 py-2 focus:border-sky-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900"
          />
          <p className="text-xs dark:text-slate-500 text-slate-400 mt-1">
            Daily reports will be sent to this email address.
          </p>
        </div>

        {/* Delivery Time */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
            <Clock className="w-4 h-4 text-slate-400" />
            Delivery Time
          </label>
          <input
            type="time"
            value={formData.delivery_time}
            onChange={(e) => setFormData(prev => ({ ...prev, delivery_time: e.target.value }))}
            className="w-full rounded-lg px-3 py-2 focus:border-sky-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900"
          />
          <p className="text-xs dark:text-slate-500 text-slate-400 mt-1">
            Time when you want to receive your daily report.
          </p>
        </div>

        {/* Timezone */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
            <Globe className="w-4 h-4 text-slate-400" />
            Timezone
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
            className="w-full rounded-lg px-3 py-2 focus:border-sky-500 focus:outline-none dark:bg-slate-800 bg-white border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900"
          >
            {TIMEZONES.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        {/* Active Toggle */}
        <div className="flex items-center justify-between p-4 dark:bg-slate-800/50 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-3">
            <Power className={`w-5 h-5 ${formData.is_active ? 'text-green-500' : 'text-slate-400'}`} />
            <div>
              <p className="font-medium dark:text-white text-slate-900">
                Enable Monitoring
              </p>
              <p className="text-sm dark:text-slate-400 text-slate-500">
                {formData.is_active ? 'Daily reports are enabled' : 'Monitoring is paused'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
            className={`relative w-12 h-7 rounded-full transition-colors ${
              formData.is_active ? 'bg-green-500' : 'dark:bg-slate-700 bg-slate-300'
            }`}
          >
            <span
              className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform shadow ${
                formData.is_active ? 'left-6' : 'left-1'
              }`}
            />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg dark:bg-red-500/10 bg-red-50 border dark:border-red-500/30 border-red-200">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm dark:text-red-400 text-red-600">{error}</p>
          </div>
        )}

        {/* Success */}
        {saveSuccess && (
          <div className="flex items-start gap-2 p-3 rounded-lg dark:bg-green-500/10 bg-green-50 border dark:border-green-500/30 border-green-200">
            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm dark:text-green-400 text-green-600">Settings saved successfully</p>
          </div>
        )}

        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="w-full px-4 py-2 bg-sky-600 hover:bg-sky-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>

      {/* Disconnect Section */}
      <div className="dark:bg-slate-900 bg-white border dark:border-red-500/30 border-red-200 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-lg dark:bg-red-500/10 bg-red-50">
            <Unplug className="w-5 h-5 text-red-500" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold dark:text-white text-slate-900 mb-1">
              Disconnect Facebook Session
            </h3>
            <p className="text-sm dark:text-slate-400 text-slate-500 mb-4">
              This will remove your Facebook session and stop all monitoring. Your sources and settings will be preserved for when you reconnect.
            </p>

            {showDisconnectConfirm ? (
              <div className="flex items-center gap-3">
                <span className="text-sm dark:text-red-400 text-red-600">Are you sure?</span>
                <button
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  className="px-4 py-2 bg-red-500 hover:bg-red-400 text-white rounded-lg font-medium text-sm flex items-center gap-2"
                >
                  {isDisconnecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    'Yes, Disconnect'
                  )}
                </button>
                <button
                  onClick={() => setShowDisconnectConfirm(false)}
                  disabled={isDisconnecting}
                  className="px-4 py-2 dark:text-slate-400 text-slate-600 hover:dark:text-white hover:text-slate-900 rounded-lg font-medium text-sm"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowDisconnectConfirm(true)}
                className="px-4 py-2 dark:bg-red-500/10 bg-red-50 dark:text-red-400 text-red-600 hover:dark:bg-red-500/20 hover:bg-red-100 rounded-lg font-medium text-sm border dark:border-red-500/30 border-red-200 transition-colors"
              >
                Disconnect Session
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FBMonitorSettings;
